import React, { Component } from 'react';
import withStyles from 'isomorphic-style-loader/withStyles';
import { connect } from 'react-redux';
import { toastr } from 'react-redux-toastr';
import cx from 'classnames';
import s from './ItemImageDropzone.css';
import { api } from '../../../../../config';
import DefaultImage from '../../../../../../public/StoreIcon/store-profile-default.svg';

const ALLOWED_TYPES = ['image/jpeg', 'image/png', 'image/jpg'];
const UPLOAD_URL = '/uploadItemImage';

class ItemImageDropzone extends Component {
  constructor(props) {
    super(props);
    this.state = {
      isDragging: false,
    };
    this.fileInputRef = React.createRef();
    this.handleDragOver = this.handleDragOver.bind(this);
    this.handleDragLeave = this.handleDragLeave.bind(this);
    this.handleDrop = this.handleDrop.bind(this);
    this.handleFileChange = this.handleFileChange.bind(this);
    this.handleClick = this.handleClick.bind(this);
    this.uploadFile = this.uploadFile.bind(this);
  }

  handleDragOver(e) {
    e.preventDefault();
    e.stopPropagation();
    this.setState({ isDragging: true });
  }

  handleDragLeave(e) {
    e.preventDefault();
    e.stopPropagation();
    // Only leave if we're exiting the drop zone itself (not a child element)
    if (!e.currentTarget.contains(e.relatedTarget)) {
      this.setState({ isDragging: false });
    }
  }

  async handleDrop(e) {
    e.preventDefault();
    e.stopPropagation();
    this.setState({ isDragging: false });

    const files = e.dataTransfer && e.dataTransfer.files;
    if (!files || files.length === 0) return;
    await this.uploadFile(files[0]);
  }

  async handleFileChange(e) {
    const files = e.target.files;
    if (!files || files.length === 0) return;
    await this.uploadFile(files[0]);
    // Reset via ref — e.target is null after await (React synthetic event pooling)
    if (this.fileInputRef.current) this.fileInputRef.current.value = '';
  }

  handleClick() {
    this.fileInputRef.current && this.fileInputRef.current.click();
  }

  async uploadFile(file) {
    const { onUploadStart, onUploadEnd, onSuccess, maxUploadSize } = this.props;

    if (!ALLOWED_TYPES.includes(file.type)) {
      toastr.error('Error!', 'Please upload a PNG or JPG image.');
      return;
    }

    const maxSize = parseInt(maxUploadSize) || 10;
    if (file.size > maxSize * 1024 * 1024) {
      toastr.error('File too large', `Maximum upload size is ${maxSize}MB.`);
      return;
    }

    if (onUploadStart) onUploadStart();

    const formData = new FormData();
    formData.append('file', file);

    try {
      const fileName = await new Promise((resolve, reject) => {
        const xhr = new XMLHttpRequest();
        xhr.open('POST', api.apiEndpoint + UPLOAD_URL);
        xhr.onload = () => {
          if (xhr.status >= 200 && xhr.status < 300) {
            try {
              const result = JSON.parse(xhr.responseText);
              if (result && result.fileName) {
                resolve(result.fileName);
              } else {
                reject(new Error('No fileName in response'));
              }
            } catch (e) {
              reject(new Error('Invalid JSON response'));
            }
          } else {
            reject(new Error(`Upload failed: ${xhr.status}`));
          }
        };
        xhr.onerror = () => reject(new Error('Network error'));
        xhr.send(formData);
      });

      if (onSuccess) onSuccess(fileName);
    } catch (err) {
      console.error('[ItemImageDropzone] upload error:', err);
      toastr.error('Upload Error', 'Failed to upload image. Please try again.');
    } finally {
      if (onUploadEnd) onUploadEnd();
    }
  }

  render() {
    const { imageUrl, maxUploadSize, label } = this.props;
    const { isDragging } = this.state;

    return (
      <div
        className={cx(s.dropZoneWrapper, { [s.dragging]: isDragging })}
        onDragOver={this.handleDragOver}
        onDragLeave={this.handleDragLeave}
        onDrop={this.handleDrop}
      >
        {/* Preview area — fixed size on the left */}
        <div className={s.previewArea}>
          {imageUrl
            ? <div className={s.previewImg} style={{ backgroundImage: `url(${imageUrl})` }} />
            : (
              <div className={s.previewPlaceholder}>
                <img src={DefaultImage} className={s.defaultImg} alt="default" />
              </div>
            )
          }
        </div>

        {/* Action area — fills remaining width on the right */}
        <div className={s.actionArea}>
          <button
            type="button"
            className={s.chooseFileBtn}
            onClick={this.handleClick}
          >
            Choose File
          </button>

          <p className={s.subText}>
            Max. {maxUploadSize || 10}MB &nbsp;·&nbsp; PNG, JPG
          </p>
        </div>

        {/* Drag overlay — covers entire wrapper */}
        {isDragging && (
          <div className={s.dragOverlay}>
            <span className={s.dragOverlayIcon}>📥</span>
            <span className={s.dragOverlayText}>Drop image here</span>
          </div>
        )}

        {/* Hidden native file input */}
        <input
          ref={this.fileInputRef}
          type="file"
          accept="image/jpeg,image/png,image/jpg"
          className={s.hiddenInput}
          onChange={this.handleFileChange}
        />
      </div>
    );
  }
}

const mapState = (state) => ({
  maxUploadSize: state.siteSettings.data.maxUploadSize,
});

export default withStyles(s)(connect(mapState)(ItemImageDropzone));
